import {
  COMPETITOR_FAVORITES_FOLDER_ID,
  CONTENT_FOLDER_ROOT_ID,
} from "./constants";

export { releaseOwnListener } from "./releaseOwnListener";

export const isCompetitorFavoritesFolder = (folderID) =>
  folderID === COMPETITOR_FAVORITES_FOLDER_ID;

export const normalizeFolderName = (name) => name?.trim() || "";

export const normalizeParentFolderID = (parentFolderID) =>
  parentFolderID || CONTENT_FOLDER_ROOT_ID;

export const isDuplicateFolderName = ({
  folders = [],
  parentFolderID,
  name,
  excludeFolderID = null,
}) => {
  const normalizedName = normalizeFolderName(name).toLocaleLowerCase();
  if (!normalizedName) return false;

  const normalizedParentID = normalizeParentFolderID(parentFolderID);

  return folders.some((folder) => {
    if (excludeFolderID && folder.id === excludeFolderID) return false;

    return (
      normalizeParentFolderID(folder.parentFolderID) === normalizedParentID &&
      normalizeFolderName(folder.name).toLocaleLowerCase() === normalizedName
    );
  });
};

export const getCommonFolderID = ({ items = [] }) => {
  if (!items.length) return null;

  const firstFolderID = items[0]?.folderID ?? null;
  const allShareFolder = items.every(
    (item) => (item?.folderID ?? null) === firstFolderID
  );

  return allShareFolder ? firstFolderID : null;
};

export const getChildFolders = ({ folders = [], parentFolderID = null }) => {
  const parentID = normalizeParentFolderID(parentFolderID);

  return folders.filter(
    (folder) => normalizeParentFolderID(folder.parentFolderID) === parentID
  );
};

export const getBreadcrumb = ({ folders = [], folderID = null }) => {
  if (!folderID) return [];

  const breadcrumb = [];
  let cursor = folderID;

  while (cursor) {
    const folder = folders.find((item) => item.id === cursor);
    if (!folder) break;

    breadcrumb.unshift(folder);
    cursor =
      normalizeParentFolderID(folder.parentFolderID) === CONTENT_FOLDER_ROOT_ID
        ? null
        : folder.parentFolderID;
  }

  return breadcrumb;
};

export const getFolderPathLabel = ({ folders = [], folderID }) => {
  return getBreadcrumb({ folders, folderID })
    .map((folder) => folder.name)
    .join(" / ");
};

export const getFolderParentName = ({ folders = [], folderID }) => {
  const folder = folders.find((item) => item.id === folderID);
  if (!folder) return null;

  const parentID = normalizeParentFolderID(folder.parentFolderID);
  if (parentID === CONTENT_FOLDER_ROOT_ID) return null;

  const parent = folders.find((item) => item.id === parentID);
  return normalizeFolderName(parent?.name) || null;
};

export const buildFolderSelectOptions = ({ folders = [] }) => {
  const nameCounts = folders.reduce((counts, folder) => {
    const key = normalizeFolderName(folder.name).toLocaleLowerCase();
    counts[key] = (counts[key] || 0) + 1;
    return counts;
  }, {});

  const options = folders.map((folder) => {
    const name = normalizeFolderName(folder.name);
    const pathLabel = getFolderPathLabel({ folders, folderID: folder.id });
    const parentName = getFolderParentName({ folders, folderID: folder.id });
    const nameKey = name.toLocaleLowerCase();
    const hasDuplicateName = nameCounts[nameKey] > 1;
    const displayLabel =
      hasDuplicateName && parentName ? `${name} · ${parentName}` : name;

    return {
      value: folder.id,
      label: displayLabel,
      name,
      pathLabel,
      parentName,
    };
  });

  return options.sort((left, right) =>
    left.pathLabel.localeCompare(right.pathLabel)
  );
};

export const filterFolderOptions = ({ options = [], inputValue = "" }) => {
  const query = inputValue.trim().toLocaleLowerCase();
  if (!query) return options;

  return options.filter((option) => {
    const name = option.name.toLocaleLowerCase();
    const path = option.pathLabel.toLocaleLowerCase();
    const parent = (option.parentName || "").toLocaleLowerCase();
    const label = option.label.toLocaleLowerCase();

    return (
      name.includes(query) ||
      path.includes(query) ||
      parent.includes(query) ||
      label.includes(query)
    );
  });
};
